import { runAgent } from './loop';
import { createWsProvider } from './provider';
import { proxyUrl } from './config';
import { useCanvasStore } from '../store/canvas-store';
import { serializeForAgent } from './serialize-for-agent';
import { subtreeHtml } from '../chrome/layerSelect';
import { getRenderHtml, framesById } from '../store/commands';
import { describeCommentTarget } from '../store/comments';
import type { CommentThread } from '../store/types';

/** Pure: assemble the scoped prompt for addressing one comment thread. */
export function buildCommentPrompt(thread: CommentThread, layerHtml: string | null): string {
  const where = describeCommentTarget(thread.target);
  const transcript = thread.messages.map((m) => `${m.author}: ${m.text}`).join('\n');
  const htmlBlock =
    layerHtml != null ? `\nCurrent HTML of that layer:\n${layerHtml}\n` : '\n';
  return (
    `[Address this feedback thread (id: ${thread.id}) anchored at ${where}.` +
    `${htmlBlock}` +
    `Thread:\n${transcript}\n\n` +
    `Edit the referenced frame to satisfy the feedback. When done, call reply_comment ` +
    `({threadId:"${thread.id}", text:"…"}) to explain what you changed, then resolve_comment ` +
    `({threadId:"${thread.id}"}).]`
  );
}

/** Run an agent turn scoped to a comment thread. Streams nothing to UI here;
 *  the agent's reply lands as a comment.reply via the reply_comment tool. */
export async function runCommentTurn(threadId: string): Promise<void> {
  const st = useCanvasStore.getState();
  const thread = (st.doc.comments ?? []).find((t) => t.id === threadId);
  if (!thread) return;

  let layerHtml: string | null = null;
  if (thread.target.kind === 'element') {
    const { frameId, nodeId } = thread.target;
    const frame = st.doc.frames.find((f) => f.id === frameId);
    if (frame) layerHtml = subtreeHtml(getRenderHtml(frame, framesById(st.doc.frames)), nodeId);
  }

  const provider = createWsProvider(proxyUrl());
  st.beginTransaction('comment turn');
  try {
    await runAgent({
      provider,
      prompt: buildCommentPrompt(thread, layerHtml),
      snapshot: serializeForAgent(useCanvasStore.getState()),
      followCamera: false,
    });
  } finally {
    st.endTransaction();
    provider.close();
  }
}
